import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { Dispatch, FunctionComponentWithRef, MouseEventHandler, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useArticleRenderContext } from "scmp-app/components/article/article-render/contexts";
import { useEnableGenericInlineWidgetByUserSubscription } from "scmp-app/components/article/article-speech/article-speech-inline-widget/variants/generic/hooks";
import { useArticleSpeechContext } from "scmp-app/components/article/article-speech/contexts";
import { textToSpeechPlayerAtom } from "scmp-app/components/text-to-speech-player/atoms";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { buttonArticleSpeechInlineWidgetGenericButtonArticle$key } from "scmp-app/queries/__generated__/buttonArticleSpeechInlineWidgetGenericButtonArticle.graphql";

import { HeadphoneIcon } from "./headphone-icon";
import {
  MainContainer,
  MinifyContainer,
  RegularContainer,
  StyledArrowRightIcon,
  StyledCloseIcon,
  Text,
} from "./styles";

export type Props = {
  entityId: string;
  extendClick?: () => void;
  isMinify?: boolean;
  keepMinifyText?: boolean;
  reference: buttonArticleSpeechInlineWidgetGenericButtonArticle$key;
  setIsTooltipActive: Dispatch<SetStateAction<boolean>>;
  withMinifyCleanUpButton?: boolean;
  withMinifyText?: boolean;
  zIndex?: string;
};

export const ArticleSpeechInlineWidgetGenericButton: FunctionComponentWithRef<
  Props,
  HTMLDivElement
> = ({
  isMinify = false,
  keepMinifyText = false,
  ref,
  withMinifyCleanUpButton = false,
  withMinifyText = true,
  ...props
}) => {
  const article = useFragment(
    graphql`
      fragment buttonArticleSpeechInlineWidgetGenericButtonArticle on Article {
        entityId
        socialHeadline
        headline
        articleSpeeches {
          url
          voiceName
        }
        images {
          isSlideshow
          url
        }
      }
    `,
    props.reference,
  );

  const { percentageReached } = useArticleRenderContext();
  const { setDurationChangeEventQueue, setTextToSpeechPlayerState, textToSpeechPlayerState } =
    useArticleSpeechContext();
  const { isWidgetEnabledByUser } = useEnableGenericInlineWidgetByUserSubscription();

  const isTargetingCurrentArticle = textToSpeechPlayerState.entityId === article.entityId;

  const isWidgetHidden = useMemo(
    () => textToSpeechPlayerState.status === "hidden" && isTargetingCurrentArticle,
    [isTargetingCurrentArticle, textToSpeechPlayerState.status],
  );
  const isWidgetPlaying = useMemo(
    () => textToSpeechPlayerState.status === "playing" && isTargetingCurrentArticle,
    [isTargetingCurrentArticle, textToSpeechPlayerState.status],
  );
  const isWidgetPaused = useMemo(
    () => textToSpeechPlayerState.status === "paused" && isTargetingCurrentArticle,
    [isTargetingCurrentArticle, textToSpeechPlayerState.status],
  );
  const [listeningDotCount, setListeningDotCount] = useState(1);

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const subscriberPhase = asyncRosettaState?.result?.tracking?.ga4.subscriberPhase;

  const handleAnalyticsEventsAndTriggerToolTip = () => {
    sendGA4Tracking({
      action: "imp",
      category: "subs",
      customized_parameters: {
        article_id: article.entityId,
        call_to_action: "subscribe",
        subscriber_phase: subscriberPhase,
        trigger_point: "text_to_speech",
      },
      subcategory: "entry_point",
    });

    props.setIsTooltipActive(true);
  };

  const handleTriggerArticleSpeechPlayer = () => {
    const mainImage = article.images?.find(image => !!image?.isSlideshow) ?? first(article.images);

    setTextToSpeechPlayerState(previousState => ({
      ...previousState,
      collapseStatus: "expanded-by-default",
      entityId: article.entityId,
      loaded: 0,
      played: 0,
      src: article.articleSpeeches?.[0]?.url,
      status: "playing",
      thumbnail: mainImage?.url ?? "",
      title: article.socialHeadline ?? article.headline ?? "",
      voiceNameInfoList: (article.articleSpeeches ?? []).map(speech => ({
        url: speech?.url ?? "",
        voiceName: speech?.voiceName ?? "",
      })),
    }));

    // The duration is not available at this point,
    // duration will be derived from the audio element after durationChange event
    setDurationChangeEventQueue(previousQueue => [
      ...previousQueue,
      (updatedDuration: number) => {
        sendGA4Tracking({
          action: "click",
          category: "tts",
          customized_parameters: {
            action_type:
              textToSpeechPlayerState.collapseStatus === "collapsed"
                ? "sidebar_button"
                : "article_button",
            article_id: article.entityId,
            audio_length: Math.ceil(updatedDuration),
            progress_fraction: 0,
          },
          subcategory: "",
        });
      },
    ]);
  };

  const handlePlayClick = () => {
    if (isWidgetPlaying || isWidgetPaused) return;

    /**
     * The play function needs to be called synchronously in the click handler
     * to avoid iOS Safari 16's `NotAllowedError`
     * see more details here: https://github.com/videojs/video.js/issues/4765
     * so we cannot wrap it with `startTransition`
     */
    if (isWidgetEnabledByUser) {
      handleTriggerArticleSpeechPlayer();
    } else {
      handleAnalyticsEventsAndTriggerToolTip();
    }
  };

  const handleHideArticleSpeechPlayerButtonClick: MouseEventHandler<SVGSVGElement> = event => {
    setTextToSpeechPlayerState(previousState => {
      sendGA4Tracking({
        action: "click",
        category: "tts",
        customized_parameters: {
          action_type: "close",
          article_id: previousState.entityId,
          audio_length: Math.ceil(previousState.duration / previousState.playbackRate),
          progress_fraction: previousState.played,
        },
        subcategory: "",
      });

      return {
        ...previousState,
        src: undefined,
        status: "hidden",
      };
    });

    event.stopPropagation();
  };

  const { isScrollingOverThreshold } = useAtomValue(textToSpeechPlayerAtom);

  useEffect(() => {
    if (
      isTargetingCurrentArticle &&
      percentageReached &&
      isScrollingOverThreshold &&
      ["paused", "playing"].includes(textToSpeechPlayerState.status) &&
      textToSpeechPlayerState.collapseStatus === "expanded-by-default"
    ) {
      setTextToSpeechPlayerState(previousState => ({
        ...previousState,
        collapseStatus: "collapsed",
      }));
    }
  }, [
    isScrollingOverThreshold,
    isTargetingCurrentArticle,
    percentageReached,
    setTextToSpeechPlayerState,
    textToSpeechPlayerState.collapseStatus,
    textToSpeechPlayerState.status,
  ]);

  useEffect(() => {
    if (!isWidgetPlaying) {
      setListeningDotCount(1);
      return;
    }

    const intervalId = window.setInterval(() => {
      setListeningDotCount(previousDotCount => (previousDotCount >= 3 ? 1 : previousDotCount + 1));
    }, 500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isWidgetPlaying]);

  const listeningText = useMemo(
    () => `Listening${".".repeat(listeningDotCount)}`,
    [listeningDotCount],
  );

  const handleRenderHeadphoneIcon = () => {
    if (keepMinifyText && (isWidgetPlaying || isWidgetPaused)) return null;
    return <HeadphoneIcon />;
  };

  const handleRenderMinifyUI = () => {
    const renderText = () => {
      if (!withMinifyText || (!keepMinifyText && (isWidgetPlaying || isWidgetPaused))) return null;
      if (isWidgetPlaying) return <Text $isListening={true}>{listeningText}</Text>;
      if (isWidgetPaused) return <Text>Paused...</Text>;
      return <Text>Listen</Text>;
    };

    const renderCleanUpButton = () => {
      if (!withMinifyCleanUpButton || isWidgetHidden || !isTargetingCurrentArticle) return null;
      return <StyledCloseIcon onClick={handleHideArticleSpeechPlayerButtonClick} />;
    };

    return (
      <MinifyContainer $withMinifyText={withMinifyText} onClick={handlePlayClick}>
        {renderText()}
        {handleRenderHeadphoneIcon()}
        {renderCleanUpButton()}
      </MinifyContainer>
    );
  };

  const handleRenderRegularUI = () => {
    const renderText = () => {
      if (isWidgetPlaying) return <Text $isListening={true}>{listeningText}</Text>;
      if (isWidgetPaused) return <Text>Paused...</Text>;
      return <Text>Listen</Text>;
    };

    const renderIcon = () => {
      if (isWidgetPlaying || isWidgetPaused) {
        return <StyledCloseIcon onClick={handleHideArticleSpeechPlayerButtonClick} />;
      }
      return <StyledArrowRightIcon />;
    };

    return (
      <RegularContainer onClick={handlePlayClick}>
        {handleRenderHeadphoneIcon()}
        {renderText()}
        {renderIcon()}
      </RegularContainer>
    );
  };

  return (
    <MainContainer {...props} $isGreyedOut={isWidgetPlaying || isWidgetPaused} ref={ref}>
      {isMinify ? handleRenderMinifyUI() : handleRenderRegularUI()}
    </MainContainer>
  );
};

ArticleSpeechInlineWidgetGenericButton.displayName = "ArticleSpeechInlineWidgetGenericButton";
